import { camelCase, keys, map } from "lodash-es";
import { IJSONAPIItem, IJSONAPIData } from "shared/types";

export interface IReducer {
  [key: string]: {
    [key: string]: IJSONAPIItem;
  };
}

// Builds complex JS object from normalized redux store.
export const build = (
  reducer: IReducer,
  objectName: string,
  itemId?: string
): any | null => {
  const obj = reducer[objectName];

  if (!obj) {
    return null;
  }

  if (!itemId) {
    const idList = keys(obj);

    if (!idList) {
      return null;
    }

    return idList.map((key) => build(reducer, objectName, key));
  }

  const target = obj[itemId];

  if (!target) {
    return null;
  }

  const { id, type, meta, links, relationships, attributes } = target;
  const result: { [key: string]: any } = {
    id,
    type: camelCase(type),
    meta,
    links,
  };

  if (attributes) {
    map(attributes, (attribute, key) => {
      if (
        (Array.isArray(attribute) && attribute[0]?.data) ||
        (!Array.isArray(attribute) &&
          typeof attribute !== "string" &&
          attribute?.data)
      ) {
        const data = buildData(attribute);
        result[key] = data;
      } else {
        result[key] = attributes[key];
      }
    });
    Object.defineProperty(result, "resolved", { value: true });
  }

  if (relationships) {
    keys(relationships).forEach((relationship) => {
      Object.defineProperty(result, relationship, {
        enumerable: true,
        get: () => {
          const field = `__${relationship}`;

          if (result[field]) {
            return result[field];
          }

          const value = buildRelationship(reducer, target, relationship);
          Object.defineProperty(result, field, { enumerable: false, value });

          return result[field];
        },
      });
    });
  }

  return result;
};

export const buildSingleItem = (target: IJSONAPIItem) => {
  const { id, meta, links, attributes } = target;
  const result: { [key: string]: any } = { id, meta, links };

  if (attributes) {
    map(attributes, (attribute, key) => {
      if (
        (Array.isArray(attribute) && attribute[0]?.data) ||
        (!Array.isArray(attribute) &&
          typeof attribute !== "string" &&
          attribute?.data)
      ) {
        const data = buildData(attribute);
        result[key] = data;
      } else {
        result[key] = attributes[key];
      }
    });
    Object.defineProperty(result, "resolved", { value: true });
  }

  return result;
};

// Helper method to build relationships between complex JS object from normalized redux store.
const buildRelationship = (
  reducer: IReducer,
  target: IJSONAPIItem,
  relationship: string
) => {
  const rel = target?.relationships?.[relationship];

  if (!rel) return null;

  if (typeof rel.data !== "undefined") {
    if (Array.isArray(rel.data)) {
      return rel.data.map(
        (child) => build(reducer, child.type, child.id) || child
      );
    }

    if (rel.data === null) {
      return null;
    }

    return build(reducer, rel.data.type, rel.data.id) || rel.data;
  }

  return undefined;
};

// Helper method to build nested data
const buildData = (data: IJSONAPIData | IJSONAPIData[]): {} | {}[] => {
  if (Array.isArray(data)) {
    return data.map((item) => buildData(item));
  }

  const { id, type, attributes } = data.data;

  const result: any = { id, type: camelCase(type) };

  if (attributes) {
    map(attributes, (attribute, key) => {
      if (
        (Array.isArray(attribute) && attribute[0]?.data) ||
        (!Array.isArray(attribute) &&
          typeof attribute !== "string" &&
          attribute?.data)
      ) {
        const data = buildData(attribute);
        result[key] = data;
      } else {
        result[key] = attributes[key];
      }
    });
    Object.defineProperty(result, "resolved", { value: true });
  }

  return result;
};
